/**
 * OIDC redirect-callback capture.
 *
 * The identity provider sends the browser back to the configured
 * `redirect_uri` with `code`/`state` on the URL. That URL is the ONLY place
 * those values exist — if anything rewrites the location before the OIDC
 * client reads them (a route guard calling `navigate('/auth/login')`, a
 * `<Navigate />` index route, a `replaceState` in a splash screen), the
 * authorization code is gone and the sign-in silently dies on the login page.
 *
 * So we capture the callback URL exactly once, as early as the first React
 * render pass, and strip the params from the address bar immediately. From
 * that point the router is free to navigate: the code lives in this module,
 * not in `window.location`.
 */

let captured: string | null = null
let hasCaptured = false

const hasAuthParams = (query: string): boolean => {
  const params = new URLSearchParams(query)
  return params.has('state') && (params.has('code') || params.has('error'))
}

/**
 * Splits a hash into its route part and its query part.
 *
 * Hash routers (`#/auth/login?code=…`) put the provider's response inside
 * the fragment, where `location.search` cannot see it.
 */
const splitHash = (hash: string): { route: string; query: string } => {
  const separator = hash.indexOf('?')
  return separator === -1
    ? { route: hash, query: '' }
    : { route: hash.slice(0, separator), query: hash.slice(separator + 1) }
}

/**
 * Reads (and consumes) the OIDC response params from the current URL.
 *
 * Idempotent: the first call decides, every later call returns the same
 * answer. Must be invoked from a render body — never an effect — so it runs
 * before route guards get a chance to navigate.
 *
 * @returns the full callback URL, or `null` when this is not a callback.
 */
export const captureOidcCallback = (): string | null => {
  if (hasCaptured) {
    return captured
  }
  hasCaptured = true

  if (typeof window === 'undefined') {
    return null
  }

  const { search, hash, href, pathname } = window.location
  const hashParts = splitHash(hash)
  const inSearch = hasAuthParams(search)
  const inHash = !inSearch && hasAuthParams(hashParts.query)

  if (!inSearch && !inHash) {
    return null
  }

  captured = href
  // Strip only the response params, preserving whichever routing scheme the
  // host app uses (browser router: pathname; hash router: `#/route`).
  window.history.replaceState({}, document.title, inSearch ? pathname : pathname + hashParts.route)

  return captured
}

/** Test seam: forget the captured callback so a spec can capture again. */
export const resetOidcCallbackCapture = (): void => {
  captured = null
  hasCaptured = false
}
